/**
 * The component writes (fieldiq-api#900): Add / Edit, CSV import, and the QR
 * label. Admin, office_staff and dispatcher only — the screens that open these
 * check `canManageDownholeInventory` first; the QR label is a read and is open
 * to every downhole role.
 *
 * Every field, its unit and its conversion come from
 * lib/downhole-component-input.ts, which is also what the CSV import reads, so
 * the form and the file cannot disagree about what a column means.
 */
import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Download, FileUp, Loader2, Printer } from 'lucide-react';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../../api/client';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useCreateComponent, useDownholeComponentQr, useImportComponents, useUpdateComponent } from '../../hooks/useDownhole';
import { instantTenantDay } from '../../lib/calendar-date';
import { csvCell } from '../../lib/csv';
import {
  DH_COMPONENT_INPUT_FIELDS,
  DH_IMPORT_MAX_ROWS,
  csvTemplateHeaders,
  inputUnit,
  parseComponentCsv,
  parseInputValue,
  type DhComponentInputField,
} from '../../lib/downhole-component-input';
import { DH_CATEGORY_LABEL, dhLabel } from '../../lib/downhole-labels';
import { formatDh, toInputValue } from '../../lib/downhole-units';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import {
  DH_COMPONENT_CATEGORIES,
  type DhComponent,
  type DhComponentImportResult,
  type DhComponentWrite,
  type DhUnitSystem,
} from '../../types/downhole';
import { DH_INPUT, DH_SECONDARY, DhDialog, DhField } from './DownholeBits';

const PRIMARY = 'fi-action fi-action--standard fi-action--primary disabled:cursor-not-allowed disabled:opacity-50';

const FIELD = (key: string) => DH_COMPONENT_INPUT_FIELDS.find((f) => f.key === key) as DhComponentInputField;

/** The form's sections, by API key. Opening balances render on create only. */
const SECTIONS: { title: string; keys: string[]; opening?: boolean }[] = [
  { title: 'Identity', keys: ['serial_number', 'category', 'description', 'manufacturer', 'model', 'year_manufactured', 'date_manufactured', 'location'] },
  { title: 'Dimensions', keys: ['od', 'id_inner', 'length_ft', 'weight_lbs', 'fish_neck_od', 'connection_top', 'connection_bottom'] },
  { title: 'Ratings', keys: ['max_temp_f', 'max_wob_klbs', 'pressure_rating'] },
  { title: 'Wear and inspection', keys: ['wear_limit_hours', 'wear_limit_runs', 'next_inspection_due'] },
  { title: 'History it arrives with', keys: ['total_run_hours', 'total_runs', 'total_footage_ft', 'last_inspection'], opening: true },
  { title: 'Notes', keys: ['notes'] },
];

/** A stored component → the text each input opens with, in the tenant's units. */
function initialValues(c: DhComponent | null, units: DhUnitSystem | null, tz: string | null): Record<string, string> {
  const out: Record<string, string> = {};
  for (const f of DH_COMPONENT_INPUT_FIELDS) {
    const v = c ? (c as unknown as Record<string, unknown>)[f.key] : null;
    if (v === null || v === undefined) out[f.key] = '';
    else if (f.quantity) out[f.key] = toInputValue(v, f.quantity, units);
    // next_inspection_due / last_inspection are instants; the picker shows the tenant's day.
    else if (f.kind === 'day') out[f.key] = instantTenantDay(v, tz) ?? '';
    else out[f.key] = String(v);
  }
  return out;
}

type Conflict =
  | { kind: 'duplicate'; message: string; existingId: string | null }
  | { kind: 'serial_other_category'; message: string; existingId: string | null };

/**
 * The two serial conflicts, by the API's machine `code` (api#913: every 409
 * carries one; the sentence is for people and may change).
 */
function conflictOf(error: unknown): Conflict | null {
  const res = (error as { response?: { status?: number; data?: Record<string, unknown> } })?.response;
  if (res?.status !== 409) return null;
  const kind = res.data?.code === 'DUPLICATE_SERIAL' ? 'duplicate' : res.data?.code === 'SERIAL_OTHER_CATEGORY' ? 'serial_other_category' : null;
  if (!kind) return null;
  return {
    kind,
    message: String(res.data?.error ?? ''),
    existingId: typeof res.data?.existing_id === 'string' ? res.data.existing_id : null,
  };
}

/**
 * Add Component (no `component`) or Edit (with one).
 *
 * EDIT SENDS ONLY WHAT CHANGED. A metric value opened and saved untouched is
 * not a round trip (toInputValue's docblock), and a PATCH carrying every field
 * would quietly rewrite lengths nobody edited. A field the reader emptied is
 * sent as `null`, which is how the API is told to clear it.
 */
export function ComponentFormDialog({
  open,
  onClose,
  units,
  tz,
  component = null,
}: {
  open: boolean;
  onClose: () => void;
  units: DhUnitSystem | null;
  tz: string | null;
  component?: DhComponent | null;
}) {
  const navigate = useNavigate();
  const create = useCreateComponent();
  const update = useUpdateComponent();
  const editing = !!component;
  const initial = useMemo(() => initialValues(component, units, tz), [component, units, tz]);
  const [values, setValues] = useState<Record<string, string>>(initial);
  const [status, setStatus] = useState(component?.status ?? '');
  const [conflict, setConflict] = useState<Conflict | null>(null);
  const busy = create.isPending || update.isPending;

  // Re-seed whenever the dialog opens on a (possibly refreshed) component.
  React.useEffect(() => {
    if (open) {
      setValues(initial);
      setStatus(component?.status ?? '');
      setConflict(null);
    }
  }, [open, initial, component?.status]);

  const set = (key: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setValues((v) => ({ ...v, [key]: e.target.value }));
    if (key === 'serial_number' || key === 'category') setConflict(null);
  };

  const build = (): DhComponentWrite | null => {
    const body: DhComponentWrite = {};
    const problems: string[] = [];
    for (const f of DH_COMPONENT_INPUT_FIELDS) {
      if (editing && f.opening) continue;
      const raw = values[f.key] ?? '';
      if (editing && raw === initial[f.key]) continue;
      if (f.required && !raw.trim()) {
        problems.push(`${f.label} is required`);
        continue;
      }
      if (f.key === 'category') {
        body.category = raw;
        continue;
      }
      const out = parseInputValue(f, raw, units);
      if ('error' in out) problems.push(out.error);
      else if (out.value !== null) body[f.key] = out.value;
      else if (editing) body[f.key] = null;
    }
    if (editing && component && status && status !== component.status) body.status = status;
    if (problems.length > 0) {
      toast.error(problems.join('. '));
      return null;
    }
    return body;
  };

  const submit = async (confirmDistinct = false) => {
    const body = build();
    if (!body) return;
    if (editing && Object.keys(body).length === 0) {
      onClose();
      return;
    }
    if (confirmDistinct) body.confirm_distinct = true;
    try {
      if (editing && component) {
        await update.mutateAsync({ id: component.id, body });
        toast.success(`${values.serial_number || component.serial_number} saved`);
        onClose();
      } else {
        const result = await create.mutateAsync(body);
        toast.success(`${result.data?.serial_number ?? body.serial_number} added to the register`);
        onClose();
        if (result.data?.id) navigate(`/downhole/components/${result.data.id}`);
      }
    } catch (error) {
      const c = conflictOf(error);
      if (c) setConflict(c);
      else toast.error(getApiErrorMessage(error, editing ? 'The component was not saved.' : 'The component was not added.'));
    }
  };

  const field = (key: string) => {
    const f = FIELD(key);
    const unit = inputUnit(f, units);
    const label = `${f.label}${unit ? ` (${unit})` : ''}${f.required ? ' *' : ''}`;
    if (key === 'category') {
      return (
        <DhField key={key} label={label}>
          <select className={DH_INPUT} value={values.category} onChange={set('category')}>
            <option value="">Choose…</option>
            {DH_COMPONENT_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {DH_CATEGORY_LABEL[c]}
              </option>
            ))}
          </select>
        </DhField>
      );
    }
    if (f.textarea) {
      return (
        <div key={key} className="sm:col-span-2">
          <DhField label={label}>
            <textarea className={`${DH_INPUT} h-24 py-2`} value={values[key]} onChange={set(key)} maxLength={f.maxLength} />
          </DhField>
        </div>
      );
    }
    return (
      <DhField
        key={key}
        label={label}
        hint={key === 'wear_limit_hours' || key === 'wear_limit_runs' ? 'Leave blank if not tracked' : undefined}
      >
        <input
          className={DH_INPUT}
          type={f.kind === 'day' ? 'date' : 'text'}
          inputMode={f.kind === 'number' ? 'decimal' : f.kind === 'integer' ? 'numeric' : undefined}
          value={values[key]}
          onChange={set(key)}
          maxLength={f.maxLength}
        />
      </DhField>
    );
  };

  const fleetStatusEditable = editing && (component?.status === 'available' || component?.status === 'retired');

  return (
    <DhDialog
      open={open}
      wide
      title={editing ? `Edit ${component?.serial_number}` : 'Add component'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className={DH_SECONDARY} onClick={onClose}>
            Cancel
          </button>
          <button type="button" className={PRIMARY} disabled={busy} onClick={() => submit(false)}>
            {busy && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
            {editing ? 'Save changes' : 'Add component'}
          </button>
        </>
      }
    >
      <div className="space-y-5">
        {conflict && (
          <div role="alert" className="rounded-fi-control border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-dark px-4 py-3 text-sm text-fieldiq-text">
            <p>{conflict.message}</p>
            <div className="mt-2 flex flex-wrap items-center gap-3">
              {conflict.existingId && (
                <Link to={`/downhole/components/${conflict.existingId}`} className="font-bold text-fieldiq-accent-signal hover:underline" onClick={onClose}>
                  Open the existing tool
                </Link>
              )}
              {conflict.kind === 'serial_other_category' && (
                <button type="button" className={DH_SECONDARY} disabled={busy} onClick={() => submit(true)}>
                  It is a different tool — save it
                </button>
              )}
            </div>
          </div>
        )}

        {SECTIONS.filter((s) => !(editing && s.opening)).map((s) => (
          <fieldset key={s.title}>
            <legend className="mb-2 text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">{s.title}</legend>
            {s.opening && (
              <p className="mb-2 text-xs text-fieldiq-muted">
                For a tool that arrives with a history. After this, these move only through runs and inspections.
              </p>
            )}
            <div className="grid gap-4 sm:grid-cols-2">{s.keys.map(field)}</div>
          </fieldset>
        ))}

        {fleetStatusEditable && (
          <fieldset>
            <legend className="mb-2 text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">In the fleet</legend>
            <div className="grid gap-4 sm:grid-cols-2">
              <DhField label="Status" hint="Every other status follows the assembly, runs and the shop.">
                <select className={DH_INPUT} value={status} onChange={(e) => setStatus(e.target.value)}>
                  <option value="available">Available</option>
                  <option value="retired">Retired</option>
                </select>
              </DhField>
            </div>
          </fieldset>
        )}
      </div>
    </DhDialog>
  );
}

/** File text; FileReader where Blob#text is missing (older WebViews, jsdom). */
function readFileText(file: File): Promise<string> {
  if (typeof file.text === 'function') return file.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file);
  });
}

/** One line of the result: a serial, linked to the tool it landed as or matched. */
function ResultLine({ id, serial, children, onNavigate }: { id: string; serial: string; children?: React.ReactNode; onNavigate: () => void }) {
  return (
    <li className="flex flex-wrap items-baseline gap-x-2 px-3 py-2 text-sm text-fieldiq-text">
      <Link to={`/downhole/components/${id}`} onClick={onNavigate} className="font-mono font-extrabold text-fieldiq-accent-signal hover:underline">
        {serial}
      </Link>
      {children}
    </li>
  );
}

/**
 * CSV import. Parsed and checked here first, so a typo is named by its
 * spreadsheet line before anything is sent; then the API validates every row
 * again and imports all or nothing. The answer is shown as the API gave it:
 * inserted, skipped as duplicates, and held for review (same serial, other
 * category) — the last two are NOT imported, and say so.
 */
export function ImportComponentsDialog({ open, onClose, units }: { open: boolean; onClose: () => void; units: DhUnitSystem | null }) {
  const importRows = useImportComponents();
  const [fileName, setFileName] = useState('');
  const [problems, setProblems] = useState<{ line: number | null; error: string }[]>([]);
  const [result, setResult] = useState<{ data: DhComponentImportResult; lines: number[] } | null>(null);

  React.useEffect(() => {
    if (open) {
      setFileName('');
      setProblems([]);
      setResult(null);
    }
  }, [open]);

  const lineOf = (lines: number[], row: number) => lines[row] ?? row + 2;

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setFileName(file.name);
    setResult(null);
    const parsed = parseComponentCsv(await readFileText(file), units);
    if (parsed.errors.length > 0) {
      setProblems(parsed.errors);
      return;
    }
    setProblems([]);
    try {
      const res = await importRows.mutateAsync(parsed.rows);
      if (res.data) setResult({ data: res.data, lines: parsed.lines });
    } catch (error) {
      const rowErrors = (error as { response?: { data?: { errors?: { row: number; error: string }[] } } })?.response?.data?.errors;
      if (Array.isArray(rowErrors)) {
        setProblems(rowErrors.map((x) => ({ line: lineOf(parsed.lines, x.row), error: x.error })));
      } else {
        setProblems([{ line: null, error: getApiErrorMessage(error, 'The file was not imported.') }]);
      }
    }
  };

  const downloadTemplate = () => {
    const blob = new Blob([`${csvTemplateHeaders(units).map(csvCell).join(',')}\n`], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'components-template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <DhDialog
      open={open}
      wide
      title="Import components"
      onClose={onClose}
      footer={
        <button type="button" className={DH_SECONDARY} onClick={onClose}>
          {result ? 'Done' : 'Cancel'}
        </button>
      }
    >
      <div className="space-y-4 text-sm text-fieldiq-text">
        <p>
          One component per row, at most {DH_IMPORT_MAX_ROWS}. <strong>serial_number</strong>, <strong>category</strong> and{' '}
          <strong>description</strong> are required. Measures are read in {units === 'metric' ? 'metric' : 'imperial'} units, as the
          template's headers show. A tool already in the register under the same serial and category is skipped, not
          duplicated.
        </p>
        <div className="flex flex-wrap gap-2">
          <button type="button" className={DH_SECONDARY} onClick={downloadTemplate}>
            <Download className="size-4" aria-hidden="true" />
            Download template
          </button>
          <label className={`${PRIMARY} cursor-pointer ${importRows.isPending ? 'pointer-events-none opacity-50' : ''}`}>
            {importRows.isPending ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <FileUp className="size-4" aria-hidden="true" />}
            Choose CSV file
            <input type="file" accept=".csv,text/csv" aria-label="CSV file" className="sr-only" onChange={onFile} disabled={importRows.isPending} />
          </label>
          {fileName && <span className="self-center text-xs text-fieldiq-muted">{fileName}</span>}
        </div>

        {problems.length > 0 && (
          <div role="alert" className="rounded-fi-control border border-fieldiq-border border-l-4 border-l-fi-red bg-fieldiq-dark">
            <p className="px-3 pt-3 font-bold">Nothing was imported. Fix these and choose the file again:</p>
            <ul className="divide-y divide-fieldiq-border">
              {problems.map((p, i) => (
                <li key={i} className="px-3 py-2">
                  {p.line !== null && <span className="mr-2 font-mono text-xs text-fieldiq-muted">Line {p.line}</span>}
                  {p.error}
                </li>
              ))}
            </ul>
          </div>
        )}

        {result && (
          <div className="space-y-3" data-testid="downhole-import-result">
            <p className="font-bold">{result.data.message}</p>
            {result.data.inserted.length > 0 && (
              <section>
                <h3 className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">
                  Imported · {result.data.counts.inserted}
                </h3>
                <ul className="mt-1 max-h-48 divide-y divide-fieldiq-border overflow-y-auto rounded-fi-control border border-fieldiq-border">
                  {result.data.inserted.map((r) => (
                    <ResultLine key={r.id} id={r.id} serial={r.serial_number} onNavigate={onClose}>
                      <span className="text-xs text-fieldiq-muted">line {lineOf(result.lines, r.row)}</span>
                    </ResultLine>
                  ))}
                </ul>
              </section>
            )}
            {result.data.skipped.length > 0 && (
              <section>
                <h3 className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">
                  Skipped, already in the register · {result.data.counts.skipped}
                </h3>
                <ul className="mt-1 max-h-48 divide-y divide-fieldiq-border overflow-y-auto rounded-fi-control border border-fieldiq-border">
                  {result.data.skipped.map((r) => (
                    <ResultLine key={`${r.row}`} id={r.existing_id} serial={r.serial_number} onNavigate={onClose}>
                      <span className="text-xs text-fieldiq-muted">
                        line {lineOf(result.lines, r.row)}
                        {r.existing_row !== undefined ? ` — repeats line ${lineOf(result.lines, r.existing_row)}` : ' — same serial and category'}
                      </span>
                    </ResultLine>
                  ))}
                </ul>
              </section>
            )}
            {result.data.review.length > 0 && (
              <section>
                <h3 className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">
                  Not imported, needs a person · {result.data.counts.review}
                </h3>
                <p className="mt-1 text-xs text-fieldiq-muted">
                  The serial already belongs to a tool of another category. If it is a different tool, add it on its own with
                  Add Component and confirm.
                </p>
                <ul className="mt-1 max-h-48 divide-y divide-fieldiq-border overflow-y-auto rounded-fi-control border border-fieldiq-border">
                  {result.data.review.map((r) => (
                    <ResultLine key={`${r.row}`} id={r.existing_id} serial={r.existing_serial_number} onNavigate={onClose}>
                      <span className="text-xs text-fieldiq-muted">
                        line {lineOf(result.lines, r.row)}: {r.serial_number} as {dhLabel(DH_CATEGORY_LABEL, r.category)} — already a{' '}
                        {dhLabel(DH_CATEGORY_LABEL, r.existing_category)}
                      </span>
                    </ResultLine>
                  ))}
                </ul>
              </section>
            )}
          </div>
        )}
      </div>
    </DhDialog>
  );
}

/**
 * The QR label: the API's PNG of the component's page, with the serial and
 * description printed beside it. Print opens the label alone in a new window
 * so the page around it does not print.
 */
export function QrLabelDialog({ open, onClose, componentId, units }: { open: boolean; onClose: () => void; componentId: string; units: DhUnitSystem | null }) {
  const query = useDownholeComponentQr(componentId, open);
  const failure = queryLoadFailure(query);
  const qr = query.data?.data ?? null;

  const print = () => {
    if (!qr) return;
    const w = window.open('', '_blank', 'width=480,height=640');
    if (!w) {
      toast.error('The print window was blocked. Allow pop-ups for this site and try again.');
      return;
    }
    const doc = w.document;
    doc.title = `QR ${qr.serial_number}`;
    const style = doc.createElement('style');
    style.textContent =
      'body{font-family:system-ui,sans-serif;margin:24px;text-align:center;color:#000;background:#fff}' +
      'img{width:240px;height:240px}h1{font-family:monospace;font-size:20px;margin:8px 0 4px}p{margin:2px 0;font-size:12px}';
    doc.head.appendChild(style);
    const img = doc.createElement('img');
    img.src = qr.png_data_url;
    img.alt = `QR code for ${qr.serial_number}`;
    const h1 = doc.createElement('h1');
    h1.textContent = qr.serial_number;
    doc.body.append(img, h1);
    for (const line of [qr.description, dhLabel(DH_CATEGORY_LABEL, qr.category), qr.company_name]) {
      if (!line) continue;
      const p = doc.createElement('p');
      p.textContent = line;
      doc.body.appendChild(p);
    }
    img.onload = () => {
      w.focus();
      w.print();
    };
  };

  return (
    <DhDialog
      open={open}
      title="QR label"
      onClose={onClose}
      footer={
        <>
          <button type="button" className={DH_SECONDARY} onClick={onClose}>
            Close
          </button>
          <button type="button" className={PRIMARY} disabled={!qr} onClick={print}>
            <Printer className="size-4" aria-hidden="true" />
            Print
          </button>
        </>
      }
    >
      {query.isLoading ? (
        <div className="flex justify-center py-10">
          <Loader2 className="size-6 animate-spin text-fieldiq-muted" aria-label="Loading QR label" />
        </div>
      ) : failure ? (
        <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="this label" />
      ) : !qr ? (
        <p className="py-6 text-center text-sm text-fieldiq-text">This component does not exist in your register.</p>
      ) : (
        <div className="flex flex-col items-center gap-2 text-center">
          {/* The code is black on white whatever the theme: a scanner needs the contrast, and it is what prints. */}
          <img src={qr.png_data_url} alt={`QR code for ${qr.serial_number}`} className="size-60 rounded-fi-control bg-white p-2" />
          <p className="font-mono text-lg font-extrabold text-white">{qr.serial_number}</p>
          <p className="text-sm text-fieldiq-text">{qr.description || '—'}</p>
          <p className="text-xs text-fieldiq-muted">
            {dhLabel(DH_CATEGORY_LABEL, qr.category)} · OD {formatDh(qr.od, 'diameter', units)}
          </p>
          <p className="break-all text-xs text-fieldiq-muted">{qr.target_url}</p>
        </div>
      )}
    </DhDialog>
  );
}
